import { useCallback, useEffect, useRef, useState } from "react";

interface UseAutoRefreshOptions {
  intervalMs: number;
  maxBackoffMs: number;
  enabled?: boolean;
  refresh: () => Promise<void>;
}

export function useAutoRefresh({ intervalMs, maxBackoffMs, enabled = true, refresh }: UseAutoRefreshOptions) {
  const refreshRef = useRef(refresh);
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const failureCountRef = useRef(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  const refreshNow = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async () => {
      setIsRefreshing(true);
      try {
        await refreshRef.current();
        failureCountRef.current = 0;
        setLastRefreshAt(new Date());
        setError(null);
        return true;
      } catch (err) {
        failureCountRef.current += 1;
        setError(err instanceof Error ? err.message : String(err));
        return false;
      } finally {
        setIsRefreshing(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let timer: number | undefined;

    function clearTimer() {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    }

    function nextDelay() {
      if (failureCountRef.current === 0) return intervalMs;
      return Math.min(maxBackoffMs, intervalMs * 2 ** failureCountRef.current);
    }

    function schedule() {
      clearTimer();
      if (document.hidden) return;
      timer = window.setTimeout(async () => {
        await refreshNow();
        schedule();
      }, nextDelay());
    }

    function handleVisibilityChange() {
      clearTimer();
      if (document.hidden) return;
      void refreshNow().then(schedule);
    }

    function handleOnline() {
      clearTimer();
      void refreshNow().then(schedule);
    }

    schedule();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [enabled, intervalMs, maxBackoffMs, refreshNow]);

  return {
    isRefreshing,
    lastRefreshAt,
    error,
    refreshNow,
  };
}
